/**
 * Pure voice-note rules for audio attachments.
 *
 * Audio attachments represent voice notes or other audio content in messages.
 * This module contains logic to determine supported MIME types, normalize
 * audio samples into a visual waveform representation, and format durations
 * for display.
 *
 * Everything here is pure — no React, no I/O, no database — so it can be
 * unit-tested without jsdom and imported by any layer.
 */

export const MAX_AUDIO_BYTES = 16_777_216; // 16 MB
export const MAX_AUDIO_SECONDS = 300; // 5 minutes
export const PEAK_BUCKETS = 40;

/**
 * Choose a MIME type for audio recording based on what the browser supports.
 * Prefers Opus in WebM (best quality and size), falls back to MP4 for Safari,
 * or returns null if nothing is supported so the caller can hide the mic.
 */
export function pickRecorderMimeType(
  isSupported: (type: string) => boolean,
): string | null {
  if (isSupported("audio/webm;codecs=opus")) return "audio/webm;codecs=opus";
  if (isSupported("audio/mp4")) return "audio/mp4";
  return null;
}

/**
 * Normalize audio samples into a waveform visualization.
 * Groups samples into buckets, takes the RMS of each bucket to represent
 * loudness, and scales so the loudest bucket is 1.0. If all samples are
 * silent (all zeros), returns an array of zeros rather than NaNs.
 * If no samples are provided, returns an empty array.
 */
export function normalisePeaks(samples: number[], buckets: number = PEAK_BUCKETS): number[] {
  if (samples.length === 0) return [];

  // Group samples into buckets
  const bucketed: number[] = Array.from({ length: buckets }, () => 0);
  const samplesPerBucket = samples.length / buckets;

  for (let i = 0; i < samples.length; i++) {
    const bucketIndex = Math.floor(i / samplesPerBucket);
    // Use RMS (root mean square) for each bucket
    bucketed[bucketIndex]! += samples[i]! * samples[i]!;
  }

  // Convert to RMS values
  const peaks = bucketed.map((sum, idx) => {
    const count = Math.min(
      Math.ceil(samplesPerBucket),
      samples.length - Math.floor(idx * samplesPerBucket),
    );
    return Math.sqrt(sum / count);
  });

  // Find max and normalize
  const max = Math.max(...peaks);
  if (max === 0) return peaks; // All silent — return zeros instead of NaN

  return peaks.map((p) => p / max);
}

/**
 * Format milliseconds as m:ss for display (e.g., "1:05" for 65 seconds).
 *
 * Hostile input is floored to "0:00" rather than rendered. `durationMs` is
 * carried in member-writable message metadata, and NaN satisfies
 * `typeof x === "number"`, so without this guard a crafted message renders
 * "NaN:NaN" (or "-1:-1") inside the other party's voice-note bubble.
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}
